/**
 * Edit Popups for Outing Detail Page
 * Tap-to-edit modals for each section of an outing
 */
import React, { useState, useEffect } from 'react';
import { X, Check, Calendar, MapPin, Settings, DollarSign, Users, Target, Edit2, Link2, Copy, Share2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Slider } from '../../../components/ui/slider';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { CourseLookupModal } from '../../../components/score-entry';
import { GAME_FORMATS as GAME_FORMATS_OBJ } from '../utils/outingHelpers';

const GAME_FORMATS = Object.entries(GAME_FORMATS_OBJ).map(([id, data]) => ({ id, ...data }));

// Base Popup Wrapper
const EditPopup = ({ children, title, onClose, onSave, saving }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 animate-fade-in">
    <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-scale-in">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Edit2 className="w-5 h-5 text-[#007AFF]" />
          <h2 className="text-lg font-bold text-white">{title}</h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
          <X className="w-5 h-5 text-zinc-400" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {children}
      </div>
      
      <div className="p-4 border-t border-white/10 flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white">
          Cancel
        </Button>
        <Button 
          onClick={onSave} 
          disabled={saving}
          className="flex-1 bg-[#007AFF] hover:bg-[#0056b3]"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  </div>
);

// Edit Event Details
export const EditEventDetailsPopup = ({ outing, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    name: outing.name || '',
    description: outing.description || '',
    event_date: outing.event_date?.split('T')[0] || '',
    tee_time: outing.tee_time || '08:00'
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(data);
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditPopup title="Edit Event Details" onClose={onClose} onSave={handleSave} saving={saving}>
      <div className="space-y-4">
        <div>
          <Label className="text-white mb-2 block">Event Name</Label>
          <Input
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="bg-[#0D0D0D] border-white/20 text-white h-12"
          />
        </div>
        
        <div>
          <Label className="text-white mb-2 block">Description</Label>
          <Textarea
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            className="bg-[#0D0D0D] border-white/20 text-white min-h-[80px]"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white mb-2 block">Event Date</Label>
            <Input
              type="date"
              value={data.event_date}
              onChange={(e) => setData({ ...data, event_date: e.target.value })}
              className="bg-[#0D0D0D] border-white/20 text-white h-12"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Tee Time</Label>
            <Input
              type="time"
              value={data.tee_time}
              onChange={(e) => setData({ ...data, tee_time: e.target.value })}
              className="bg-[#0D0D0D] border-white/20 text-white h-12"
            />
          </div>
        </div>
      </div>
    </EditPopup>
  );
};

// Edit Course
export const EditCoursePopup = ({ outing, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  const [data, setData] = useState({
    course_name: outing.course_name || '',
    course_id: outing.course_id || null,
    hole_pars: outing.hole_pars || [4,4,3,4,5,4,3,4,5,4,4,3,4,5,4,3,4,5]
  });

  const handleCourseSelect = (course) => {
    setData({
      ...data,
      course_name: course.name,
      course_id: course.id,
      hole_pars: course.holes?.map(h => h.par || 4) || data.hole_pars
    });
    setShowCourseLookup(false);
  };

  const updatePar = (index, value) => {
    const newPars = [...data.hole_pars];
    newPars[index] = Math.max(3, Math.min(5, parseInt(value) || 4));
    setData({ ...data, hole_pars: newPars });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(data);
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const front9Par = data.hole_pars.slice(0, 9).reduce((a, b) => a + b, 0);
  const back9Par = data.hole_pars.slice(9).reduce((a, b) => a + b, 0);

  return (
    <EditPopup title="Edit Course" onClose={onClose} onSave={handleSave} saving={saving}>
      <div className="space-y-4">
        <div 
          onClick={() => setShowCourseLookup(true)}
          className="p-4 rounded-xl border-2 border-dashed border-white/20 hover:border-[#007AFF]/50 cursor-pointer transition-all"
        >
          {data.course_name ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-green-400" />
                <span className="text-white font-semibold">{data.course_name}</span>
              </div>
              <Badge variant="outline" className="text-zinc-400">Change</Badge>
            </div>
          ) : (
            <div className="text-center">
              <MapPin className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
              <p className="text-zinc-400">Tap to search courses</p>
            </div>
          )}
        </div>

        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold">Hole Pars</h4>
              <div className="flex gap-2 text-sm">
                <Badge className="bg-white/10">F9: {front9Par}</Badge>
                <Badge className="bg-white/10">B9: {back9Par}</Badge>
                <Badge className="bg-green-500/20 text-green-400">Total: {front9Par + back9Par}</Badge>
              </div>
            </div>
            
            <div className="grid grid-cols-9 gap-1">
              {data.hole_pars.map((par, i) => (
                <div key={i} className="text-center">
                  <span className="text-[10px] text-zinc-500">{i + 1}</span>
                  <Input
                    type="number"
                    value={par}
                    onChange={(e) => updatePar(i, e.target.value)}
                    className="bg-[#1A1A1A] border-white/10 text-white text-center h-8 p-0 text-sm"
                    min={3}
                    max={5}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {showCourseLookup && (
        <CourseLookupModal
          onClose={() => setShowCourseLookup(false)}
          onSelect={handleCourseSelect}
        />
      )}
    </EditPopup>
  );
};

// Edit Game Format
export const EditGameFormatPopup = ({ outing, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    front_9_format: outing.game_settings?.front_9_format || 'scramble',
    back_9_format: outing.game_settings?.back_9_format || 'scramble',
    same_format_all: outing.game_settings?.same_format_all !== false,
    team_size: outing.game_settings?.team_size || 4
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ game_settings: { ...outing.game_settings, ...data } });
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditPopup title="Edit Game Format" onClose={onClose} onSave={handleSave} saving={saving}>
      <div className="space-y-4">
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-semibold">Split Format</h4>
                <p className="text-xs text-zinc-400">Different format for Front 9 vs Back 9</p>
              </div>
              <Switch
                checked={!data.same_format_all}
                onCheckedChange={(v) => setData({ ...data, same_format_all: !v })}
              />
            </div>
          </CardContent>
        </Card>

        <div>
          <Label className="text-white mb-2 block">
            {data.same_format_all ? 'Game Format' : 'Front 9 Format'}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {GAME_FORMATS.map(fmt => (
              <button
                key={fmt.id}
                onClick={() => setData({ ...data, front_9_format: fmt.id })}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  data.front_9_format === fmt.id
                    ? 'border-[#007AFF] bg-[#007AFF]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-xl mb-1 block">{fmt.icon}</span>
                <span className="text-white font-semibold text-sm">{fmt.name}</span>
              </button>
            ))}
          </div>
        </div>

        {!data.same_format_all && (
          <div>
            <Label className="text-white mb-2 block text-purple-400">Back 9 Format</Label>
            <div className="grid grid-cols-2 gap-2">
              {GAME_FORMATS.map(fmt => (
                <button
                  key={fmt.id}
                  onClick={() => setData({ ...data, back_9_format: fmt.id })}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    data.back_9_format === fmt.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <span className="text-xl mb-1 block">{fmt.icon}</span>
                  <span className="text-white font-semibold text-sm">{fmt.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="text-white mb-2 block">Team Size</Label>
          <div className="flex gap-3">
            {[2, 4].map(size => (
              <Button
                key={size}
                variant={data.team_size === size ? 'default' : 'outline'}
                className={`flex-1 h-12 ${data.team_size === size ? 'bg-[#007AFF]' : 'border-white/20 text-white'}`}
                onClick={() => setData({ ...data, team_size: size })}
              >
                {size}-Man Teams
              </Button>
            ))}
          </div>
        </div>
      </div>
    </EditPopup>
  );
};

// Edit Prizes (Skins & CTP)
export const EditPrizesPopup = ({ outing, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    skins: {
      enabled: outing.game_settings?.skins?.enabled || false,
      total_pot: outing.game_settings?.skins?.total_pot || 90,
      carryover: outing.game_settings?.skins?.carryover !== false
    },
    ctp: {
      enabled: outing.game_settings?.ctp?.enabled || false,
      holes: outing.game_settings?.ctp?.holes || [],
      total_pot: outing.game_settings?.ctp?.total_pot || 100
    }
  });

  const holePars = outing.hole_pars || [4,4,3,4,5,4,3,4,5,4,4,3,4,5,4,3,4,5];

  const toggleCTPHole = (hole) => {
    const holes = data.ctp.holes;
    const newHoles = holes.includes(hole)
      ? holes.filter(h => h !== hole)
      : [...holes, hole].sort((a, b) => a - b);
    setData({ ...data, ctp: { ...data.ctp, holes: newHoles } });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ game_settings: { ...outing.game_settings, skins: data.skins, ctp: data.ctp } });
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditPopup title="Edit Prizes" onClose={onClose} onSave={handleSave} saving={saving}>
      <div className="space-y-4">
        {/* Skins */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Skins Game</h4>
                  <p className="text-xs text-zinc-400">Lowest score wins each hole</p>
                </div>
              </div>
              <Switch
                checked={data.skins.enabled}
                onCheckedChange={(v) => setData({ ...data, skins: { ...data.skins, enabled: v } })}
              />
            </div>
            
            {data.skins.enabled && (
              <div className="space-y-3 pt-3 border-t border-white/10">
                <div>
                  <Label className="text-white text-sm">Total Pot: ${data.skins.total_pot}</Label>
                  <Slider
                    value={[data.skins.total_pot]}
                    onValueChange={(v) => setData({ ...data, skins: { ...data.skins, total_pot: v[0] } })}
                    min={18}
                    max={900}
                    step={18}
                    className="mt-2"
                  />
                  <p className="text-xs text-zinc-500 mt-1">= ${(data.skins.total_pot / 18).toFixed(2)} per hole</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Carryover on ties</span>
                  <Switch
                    checked={data.skins.carryover}
                    onCheckedChange={(v) => setData({ ...data, skins: { ...data.skins, carryover: v } })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTP */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Closest to Pin</h4>
                  <p className="text-xs text-zinc-400">Par 3 hole contests</p>
                </div>
              </div>
              <Switch
                checked={data.ctp.enabled}
                onCheckedChange={(v) => setData({ ...data, ctp: { ...data.ctp, enabled: v } })}
              />
            </div>
            
            {data.ctp.enabled && (
              <div className="space-y-3 pt-3 border-t border-white/10">
                <div>
                  <Label className="text-white text-sm mb-2 block">CTP Holes (Par 3s)</Label>
                  <div className="flex flex-wrap gap-2">
                    {holePars.map((par, i) => {
                      const hole = i + 1;
                      if (par !== 3) return null;
                      return (
                        <button
                          key={hole}
                          onClick={() => toggleCTPHole(hole)}
                          className={`px-3 py-1 rounded-full text-sm font-semibold transition-all ${
                            data.ctp.holes.includes(hole)
                              ? 'bg-yellow-500 text-black'
                              : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                          }`}
                        >
                          Hole {hole}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label className="text-white text-sm">Total Pot: ${data.ctp.total_pot}</Label>
                  <Slider
                    value={[data.ctp.total_pot]}
                    onValueChange={(v) => setData({ ...data, ctp: { ...data.ctp, total_pot: v[0] } })}
                    min={0}
                    max={500}
                    step={25}
                    className="mt-2"
                  />
                  {data.ctp.holes.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">
                      = ${(data.ctp.total_pot / data.ctp.holes.length).toFixed(2)} per hole
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </EditPopup>
  );
};

// Edit Entry Fee
export const EditEntryFeePopup = ({ outing, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [entryFee, setEntryFee] = useState(outing.entry_fee || 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ entry_fee: entryFee });
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditPopup title="Edit Entry Fee" onClose={onClose} onSave={handleSave} saving={saving}>
      <div className="space-y-4">
        <div className="text-center py-8">
          <span className="text-6xl font-black text-[#007AFF]">${entryFee}</span>
          <p className="text-zinc-400 text-sm mt-2">per player</p>
        </div>
        
        <Slider
          value={[entryFee]}
          onValueChange={(v) => setEntryFee(v[0])}
          min={0}
          max={500}
          step={5}
        />
        <div className="flex justify-between text-xs text-zinc-500">
          <span>Free</span>
          <span>$500</span>
        </div>
      </div>
    </EditPopup>
  );
};

// Share/Invite Link Popup
export const ShareInviteLinkPopup = ({ outing, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  // Generate shareable link
  const baseUrl = window.location.origin;
  const inviteLink = `${baseUrl}/outings/join/${outing.id}`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${outing.name}`,
          text: `You're invited to ${outing.name} at ${outing.course_name || 'TBD'}!`,
          url: inviteLink
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-md overflow-hidden animate-scale-in">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#007AFF]" />
            <h2 className="text-lg font-bold text-white">Share Invite Link</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <p className="text-sm text-zinc-400">
            Share this link with players to let them join your outing directly.
          </p>
          
          <div className="bg-[#0D0D0D] rounded-xl p-4 flex items-center gap-3">
            <div className="flex-1 overflow-hidden">
              <p className="text-xs text-zinc-500 mb-1">Invite Link</p>
              <p className="text-white text-sm truncate font-mono">{inviteLink}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={copyLink}
              className={copied ? 'text-green-400' : 'text-[#007AFF]'}
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={copyLink}
              className="border-white/20 text-white"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button
              onClick={shareLink}
              className="bg-[#007AFF] hover:bg-[#0056b3]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          
          <div className="pt-3 border-t border-white/10">
            <p className="text-xs text-zinc-500 text-center">
              Players who open this link can register for the outing and fill in their details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
